import React, { useEffect, useMemo, useState } from "react";
import "./index.css";
import Invoiceicon from "../../../assets/img/totalCountsIcon.png";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setProjectId as setAnalyzeProjectId,
  setMonth as setAnalyzeMonth,
  setYear as setAnalyzeYear,
  fetchDocCounts,
  fetchCodeCounts,
  fetchHighlightCounts,
  fetchCodesDescriptorCount,
  fetchCodesFlat,
  fetchDescriptorCodesCount,
  fetchDocumentsDropdown,
  fetchCodeDensity,
  fetchCodingRichness,
  fetchDocumentCoverage,
  fetchHighlightDistribution,
} from "./analyze.slice";

import {
  setProjectId as setDescriptorsProjectId,
  fetchSets,
  selectSet,
  fetchFields,
} from "../Descriptors/descriptors.slice";

import HighlightDistributionByLength from "./HighlightDistributionByLength";

type PanelKey =
  | "docType"
  | "docCode"
  | "docHighlight"
  | "codesXdescriptors"
  | "descriptorsXcodes";

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={`eye ${open ? "eye-open" : ""}`}
  >
    <path
      d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="12" cy="12" r="3.25" fill="currentColor" />
  </svg>
);

const SectionShell: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  actions: React.ReactNode;
}> = ({ title, isOpen, onToggle, children, actions }) => (
  <div className="panel">
    <div className="panel-header">
      <button
        className="panel-title"
        onClick={onToggle}
        type="button"
        style={{ border: "unset", background: "unset" }}
      >
        {title}
      </button>
      <div className="panel-header-actions">
        <div className="panel-actions">{actions}</div>
        <button
          className="panel-eye"
          onClick={onToggle}
          type="button"
          aria-label="toggle"
          style={{ border: "unset", background: "unset" }}
        >
          <EyeIcon open={isOpen} />
        </button>
      </div>
    </div>
    {isOpen && <div className="panel-body">{children}</div>}
  </div>
);

const BarWithTooltip: React.FC<{
  label: string;
  value: number;
  total: number;
  tooltipTitle?: string;
  tooltipMode?: "default" | "density";
  tooltipRightText?: string;
}> = ({
  label,
  value,
  total,
  tooltipTitle,
  tooltipMode = "default",
  tooltipRightText,
}) => {
  const { t } = useTranslation("docProcessing");
  const pct = Math.max(
    0,
    Math.min(100, total ? Math.round((value / total) * 100) : 0),
  );

  return (
    <div className="bar">
      <div className="bar-label">{label}</div>
      <div className="bar-track">
        <div
          className="bar-fill"
          data-wide={pct >= 85}
          style={{ width: `${pct}%` }}
          tabIndex={0}
        >
          <div className="bar-tooltip" role="tooltip">
            <img src={Invoiceicon} alt="" style={{ width: 38, height: 38 }} />
            <div className="tt-text">
              {tooltipMode === "density" ? (
                <div className="tt-inline">
                  <div className="tt-value">{value}</div>
                  <div className="tt-title">{tooltipRightText}</div>
                </div>
              ) : (
                <>
                  <div className="tt-title">
                    {tooltipTitle ??
                      t("analyze.totalOf", { label: label.toLowerCase() })}
                  </div>
                  <div className="tt-value">{value}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Analyze: React.FC = () => {
  const { t } = useTranslation("docProcessing");
  const [open, setOpen] = useState<Record<PanelKey, boolean>>({
    docType: true,
    docCode: false,
    docHighlight: false,
    codesXdescriptors: true,
    descriptorsXcodes: true,
  });

  const toggle = (key: PanelKey) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  const [selectedDoc, setSelectedDoc] = useState<string>("");
  const [selectedCoverageDoc, setSelectedCoverageDoc] = useState<string>("");

  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  const {
    month,
    year,
    counts,
    codes,
    highlights,
    codesDescriptor,
    codesDropdown,
    descriptorCodes,
    documentsDropdown,
    codeDensity,
    codingRichness,
    documentCoverage,
    highlightDistribution,
  } = useAppSelector((s: any) => s.analyze);

  const {
    projectId: descProjectId,
    selectedSetId,
    sets: { items: descriptorSets, loading: setsLoading },
    fields: { items: descriptorFields, loading: fieldsLoading },
  } = useAppSelector((s: any) => s.descriptors);

  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [selectedCodeId, setSelectedCodeId] = useState<string>("");

  const mockCodes = useMemo(
    () => [
      { id: "c-1", name: "Accessibility" },
      { id: "c-2", name: "Security" },
      { id: "c-3", name: "Performance" },
    ],
    [],
  );

  useEffect(() => {
    if (!projectId) return;
    dispatch(setAnalyzeProjectId(projectId));
    if (projectId !== descProjectId)
      dispatch(setDescriptorsProjectId(projectId));
  }, [dispatch, projectId, descProjectId]);

  const now = useMemo(() => new Date(), []);
  const [localMonth, setLocalMonth] = useState<number | undefined>(
    now.getMonth() + 1,
  );
  const [localYear, setLocalYear] = useState<number | undefined>(
    now.getFullYear(),
  );

  useEffect(() => {
    dispatch(setAnalyzeMonth(localMonth));
    dispatch(setAnalyzeYear(localYear));
  }, [dispatch, localMonth, localYear]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchDocCounts());
  }, [dispatch, projectId, month, year]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchCodeCounts());
    dispatch(fetchHighlightCounts());
    dispatch(fetchHighlightDistribution());
  }, [dispatch, projectId]);

  const total = (counts.internal || 0) + (counts.external || 0);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchSets());
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!selectedSetId) return;
    dispatch(fetchFields(selectedSetId));
    setSelectedFieldId("");
  }, [dispatch, selectedSetId]);

  const selectedSet = useMemo(
    () => descriptorSets.find((s: any) => s.id === selectedSetId) || null,
    [descriptorSets, selectedSetId],
  );

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchCodesFlat());
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!selectedFieldId) return;
    dispatch(fetchCodesDescriptorCount({ set_field_id: selectedFieldId }));
  }, [dispatch, selectedFieldId]);

  useEffect(() => {
    if (!selectedCodeId) return;
    dispatch(fetchDescriptorCodesCount({ code_id: selectedCodeId }));
  }, [dispatch, selectedCodeId]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchDocumentsDropdown());
    dispatch(fetchCodingRichness());
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!selectedDoc) return;
    dispatch(fetchCodeDensity({ document_id: selectedDoc }));
  }, [dispatch, selectedDoc]);

  useEffect(() => {
    if (!selectedCoverageDoc) return;
    dispatch(fetchDocumentCoverage({ document_id: selectedCoverageDoc }));
  }, [dispatch, selectedCoverageDoc]);

  const monthNames = t("analyze.months", {
    returnObjects: true,
  }) as string[];

  const years = Array.from({ length: 8 }, (_, i) => now.getFullYear() - 4 + i);

  const selectedCoverageDocName = useMemo(() => {
    const d = documentsDropdown.items.find(
      (x: any) => x.id === selectedCoverageDoc,
    );
    return d?.name || t("analyze.documentNamePlaceholder");
  }, [documentsDropdown.items, selectedCoverageDoc, t]);

  return (
    <div className="analyze-container">
      <h1>{t("analyze.chartSelector")}</h1>

          <SectionShell
            title={t("analyze.sections.documentType")}
            isOpen={open.docType}
            onToggle={() => toggle("docType")}
            actions={
              <div className="panel-action-forDocsType">
                <select
                  className="select-input select-input--mobile"
                  value={localMonth ?? ""}
                  onChange={(e) =>
                    setLocalMonth(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">{t("analyze.monthPlaceholder")}</option>
                  {monthNames.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>

                <select
                  className="select-input select-input--mobile"
                  value={localYear ?? ""}
                  onChange={(e) =>
                    setLocalYear(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">{t("analyze.yearPlaceholder")}</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            <div className="chart card">
              <div className="chart-frame">
                {counts.loading && (
                  <span className="muted">{t("common.loading")}</span>
                )}
                {counts.error && <span className="error">{counts.error}</span>}
                <BarWithTooltip
                  label={t("analyze.internalDocuments")}
                  value={counts.internal}
                  total={total || 1}
                />
                <BarWithTooltip
                  label={t("analyze.externalDocuments")}
                  value={counts.external}
                  total={total || 1}
                />
              </div>
            </div>
          </SectionShell>

          <SectionShell
            title={t("analyze.sections.documentAndCode")}
            isOpen={open.docCode}
            onToggle={() => toggle("docCode")}
            actions={<></>}
          >
            <div className="chart card">
              <div className="chart-frame">
                {codes.loading && (
                  <span className="muted">{t("common.loading")}</span>
                )}
                {codes.error && <span className="error">{codes.error}</span>}
                {!codes.loading && !codes.error && codes.rows.length === 0 && (
                  <span className="muted">{t("common.noData")}</span>
                )}
                {codes.rows.map((row: any) => (
                  <BarWithTooltip
                    key={row.document_id}
                    label={row.document_name || t("analyze.documentNamePlaceholder")}
                    value={row.count_code || 0}
                    total={codes.max || 1}
                    tooltipTitle={t("analyze.code")}
                  />
                ))}
              </div>
            </div>

            <div className="chart card" style={{ marginTop: 16 }}>
              <div className="chart-frame" style={{ textAlign: "right" }}>
                <div className="chart-frame-codeDensityInnersection">
                  <h3>{t("analyze.codeDensity")}</h3>

                  <select
                    className="select-input select-input--mobile"
                    value={selectedDoc}
                    onChange={(e) => setSelectedDoc(e.target.value)}
                    disabled={documentsDropdown.loading}
                  >
                    <option value="">{t("analyze.chooseDocument")}</option>
                    {documentsDropdown.items.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>

                  {documentsDropdown.loading && (
                    <span
                      className="muted"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      {t("analyze.loadingDocuments")}
                    </span>
                  )}
                  {documentsDropdown.error && (
                    <span
                      className="error"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      {documentsDropdown.error}
                    </span>
                  )}
                </div>

                {selectedDoc && (
                  <>
                    {codeDensity.loading && (
                      <span
                        className="muted"
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {t("analyze.loadingCodeDensity")}
                      </span>
                    )}
                    {codeDensity.error && (
                      <span
                        className="error"
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {codeDensity.error}
                      </span>
                    )}
                    {!codeDensity.loading && !codeDensity.error && (
                      <BarWithTooltip
                        label={
                          codeDensity.document_name ||
                          t("analyze.documentNamePlaceholder")
                        }
                        value={Number(
                          codeDensity.code_density_per_1000_words || 0,
                        )}
                        total={100}
                        tooltipMode="density"
                        tooltipRightText={
                          codeDensity.document_name ||
                          t("analyze.documentNamePlaceholder")
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="chart card" style={{ marginTop: 16 }}>
              <div className="chart-frame">
                <h3 className="codeRichnessTitle">{t("analyze.codeRichness")}</h3>
                {codingRichness.loading && (
                  <span className="muted">{t("common.loading")}</span>
                )}
                {codingRichness.error && (
                  <span className="error">{codingRichness.error}</span>
                )}
                {!codingRichness.loading &&
                  !codingRichness.error &&
                  codingRichness.rows.length === 0 && (
                    <span className="muted">{t("common.noData")}</span>
                  )}

                {codingRichness.rows.map((doc: any) => (
                  <div key={doc.document_id} style={{ marginBottom: 12 }}>
                    <strong>
                      {doc.document_name || t("analyze.documentFallback")}
                    </strong>
                    <BarWithTooltip
                      label={t("analyze.exclusiveCodes")}
                      value={Number(doc.exclusive_codes_count || 0)}
                      total={codingRichness.maxExclusive || 1}
                      tooltipTitle={t("analyze.exclusiveCodes")}
                    />
                    <BarWithTooltip
                      label={t("analyze.sharedCodes")}
                      value={Number(doc.shared_codes_count || 0)}
                      total={codingRichness.maxShared || 1}
                      tooltipTitle={t("analyze.sharedCodes")}
                    />
                  </div>
                ))}
              </div>
            </div>
          </SectionShell>

          <SectionShell
            title={t("analyze.sections.documentAndHighlights")}
            isOpen={open.docHighlight}
            onToggle={() => toggle("docHighlight")}
            actions={<></>}
          >
            <div className="chart card">
              <div className="chart-frame">
                {highlights.loading && (
                  <span className="muted">{t("common.loading")}</span>
                )}
                {highlights.error && (
                  <span className="error">{highlights.error}</span>
                )}
                {!highlights.loading &&
                  !highlights.error &&
                  highlights.rows.length === 0 && (
                    <span className="muted">{t("common.noData")}</span>
                  )}
                {highlights.rows.map((row: any) => (
                  <BarWithTooltip
                    key={row.document_id}
                    label={row.document_name || t("analyze.documentNamePlaceholder")}
                    value={row.count_highlights || 0}
                    total={highlights.max || 1}
                    tooltipTitle={t("analyze.highlights")}
                  />
                ))}
              </div>
            </div>

            <div className="chart card" style={{ marginTop: 16 }}>
              <div className="chart-frame">
                <div className="chart-frame-firstInnersection">
                  <h3>{t("analyze.documentCoverageTitle")}</h3>
                  <select
                    className="select-input select-input--mobile"
                    value={selectedCoverageDoc}
                    onChange={(e) => setSelectedCoverageDoc(e.target.value)}
                    disabled={documentsDropdown.loading}
                  >
                    <option value="">{t("analyze.documentNamePlaceholder")}</option>
                    {documentsDropdown.items.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                {documentsDropdown.loading && (
                  <span
                    className="muted"
                    style={{ display: "block", marginTop: 8 }}
                  >
                    {t("analyze.loadingDocuments")}
                  </span>
                )}
                {documentsDropdown.error && (
                  <span
                    className="error"
                    style={{ display: "block", marginTop: 8 }}
                  >
                    {documentsDropdown.error}
                  </span>
                )}

                {selectedCoverageDoc && (
                  <>
                    {documentCoverage.loading && (
                      <span
                        className="muted"
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {t("analyze.loadingDocumentCoverage")}
                      </span>
                    )}
                    {documentCoverage.error && (
                      <span
                        className="error"
                        style={{ display: "block", marginTop: 8 }}
                      >
                        {documentCoverage.error}
                      </span>
                    )}
                    {!documentCoverage.loading && !documentCoverage.error && (
                      <BarWithTooltip
                        label={selectedCoverageDocName}
                        value={Number(documentCoverage.coverage_percent || 0)}
                        total={100}
                        tooltipTitle={t("analyze.highlightedContentTooltip", {
                          percent: Math.round(
                            Number(documentCoverage.coverage_percent || 0),
                          ),
                          words: documentCoverage.word_count || 0,
                          highlighted: documentCoverage.highlighted_words || 0,
                        })}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <HighlightDistributionByLength
              loading={highlightDistribution.loading}
              error={highlightDistribution.error}
              rows={highlightDistribution.rows}
              maxX={60}
            />
          </SectionShell>

          <SectionShell
            title={t("analyze.sections.codesXdescriptors")}
            isOpen={open.codesXdescriptors}
            onToggle={() => toggle("codesXdescriptors")}
            actions={
              <div className="panel-action-forCodesDescriptors">
                <select
                  className="select-input select-input--mobile"
                  value={selectedSetId ?? ""}
                  onChange={(e) => dispatch(selectSet(e.target.value || null))}
                  disabled={setsLoading}
                >
                  <option value="">{t("analyze.chooseSet")}</option>
                  {descriptorSets.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <select
                  className="select-input select-input--mobile"
                  value={selectedFieldId}
                  onChange={(e) => setSelectedFieldId(e.target.value)}
                  disabled={!selectedSet || fieldsLoading}
                >
                  <option value="">{t("analyze.chooseFieldOption")}</option>
                  {descriptorFields.map((f: any) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            <div className="chart card">
              <div className="chart-frame">
                {(setsLoading || fieldsLoading) && (
                  <span className="muted">{t("common.loading")}</span>
                )}
                {!setsLoading && !fieldsLoading && !selectedSet && (
                  <span className="muted">{t("analyze.chooseSetToSeeFields")}</span>
                )}

                {selectedSet && selectedFieldId && (
                  <>
                    {codesDescriptor.loading && (
                      <span className="muted">{t("common.loading")}</span>
                    )}
                    {codesDescriptor.error && (
                      <span className="error">{codesDescriptor.error}</span>
                    )}
                    {!codesDescriptor.loading &&
                      !codesDescriptor.error &&
                      codesDescriptor.items.length === 0 && (
                        <span className="muted">{t("common.noData")}</span>
                      )}

                    {codesDescriptor.items.map((code: any) => {
                      const max =
                        Math.max(
                          1,
                          ...code.descriptors.map((d: any) =>
                            Number(d.total_count || 0),
                          ),
                        ) || 1;

                      return (
                        <div
                          key={code.code_id}
                          className="subchart codes-descriptors"
                        >
                          <div className="subchart-title">
                            <strong>
                              {code.code_name || t("analyze.codeFallback")}
                            </strong>
                          </div>

                          {code.descriptors.map((d: any, i: number) => (
                            <BarWithTooltip
                              key={`${code.code_id}-${i}`}
                              label={d.descriptor_value || t("analyze.fieldOptionFallback")}
                              value={Number(d.total_count || 0)}
                              total={max}
                              tooltipTitle={t("analyze.totalCount")}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </SectionShell>

          <SectionShell
            title={t("analyze.sections.descriptorsXcodes")}
            isOpen={open.descriptorsXcodes}
            onToggle={() => toggle("descriptorsXcodes")}
            actions={
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <select
                  className="select-input select-input--mobile"
                  value={selectedCodeId}
                  onChange={(e) => setSelectedCodeId(e.target.value)}
                  disabled={codesDropdown.loading}
                >
                  <option value="">{t("analyze.chooseCode")}</option>
                  {(codesDropdown.items.length
                    ? codesDropdown.items
                    : mockCodes
                  ).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            <div className="chart card">
              <div className="chart-frame">
                {(setsLoading || fieldsLoading) && (
                  <span className="muted">{t("common.loading")}</span>
                )}

                {selectedCodeId && (
                  <>
                    {descriptorCodes.groups.length > 0 ? (
                      descriptorCodes.groups.map((g: any, idx: any) => {
                        const gMax =
                          g.descriptor_value.reduce(
                            (m: any, it: any) =>
                              Math.max(m, Number(it.total_count) || 0),
                            1,
                          ) || 1;

                        return (
                          <div key={idx} className="subchart">
                            <div className="subchart-title descriptors-X-codes">
                              <strong>
                                {g.Set} & {g.Field}
                              </strong>
                            </div>

                            {g.descriptor_value.map((it: any, i: any) => (
                              <BarWithTooltip
                                key={`${idx}-${i}`}
                                label={it.descriptor_value || t("analyze.fieldOptionFallback")}
                                value={Number(it.total_count || 0)}
                                total={gMax}
                                tooltipTitle={t("analyze.totalCount")}
                              />
                            ))}
                          </div>
                        );
                      })
                    ) : (
                      <div className="subchart">
                        <div className="subchart-title">
                          <strong>
                            {codesDropdown.items.find(
                              (x: any) => x.id === selectedCodeId,
                            )?.name || t("analyze.codeFallback")}
                          </strong>
                        </div>

                        {descriptorCodes.loading && (
                          <span className="muted">{t("common.loading")}</span>
                        )}
                        {descriptorCodes.error && (
                          <span className="error">{descriptorCodes.error}</span>
                        )}
                        {!descriptorCodes.loading &&
                          !descriptorCodes.error &&
                          descriptorCodes.rows.length === 0 && (
                            <span className="muted">{t("common.noData")}</span>
                          )}

                        {descriptorCodes.rows.map((r: any, i: number) => (
                          <BarWithTooltip
                            key={i}
                            label={r.descriptor_value || t("analyze.fieldOptionFallback")}
                            value={Number(r.total_count || 0)}
                            total={descriptorCodes.max || 1}
                            tooltipTitle={t("analyze.totalCount")}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </SectionShell>
    </div>
  );
};

export default Analyze;
